import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import {
  ArrowLeft,
  Save,
  Loader2,
  Zap,
  Clock,
  Hand,
  Mail,
  FileText,
  MessageSquare,
  RefreshCw,
  User,
  Globe,
  Plus,
  Trash2,
  GripVertical,
} from "lucide-react";
import toast from "react-hot-toast";
import clsx from "clsx";

interface WorkflowAction {
  type: "send_email" | "generate_pdf" | "slack_notification" | "change_status" | "assign_guide" | "webhook";
  template_id?: string;
  to?: "customer" | "admin" | "guide";
  document_type?: "contract" | "invoice";
  send_email?: boolean;
  message_template?: string;
  new_status?: string;
  guide_id?: string;
  url?: string;
  method?: "POST" | "GET";
  body_template?: string;
}

interface Workflow {
  id: string;
  name: string;
  description?: string;
  triggerType: "status_change" | "time_based" | "manual";
  triggerConfig: {
    from_status?: string[];
    to_status?: string[];
    reference_field?: string;
    days_offset?: number;
    time_of_day?: string;
    only_if_status?: string[];
  } | null;
  conditions?: Array<{
    field: string;
    operator: string;
    value: unknown;
  }>;
  actions: WorkflowAction[];
  isActive: boolean;
  priority: number;
}

interface Template {
  id: string;
  name: string;
  type: "email" | "pdf";
  category: string;
}

const ORDER_STATUSES = [
  { value: "inquiry", label: "Poptávka" },
  { value: "quoted", label: "Nabídka odeslána" },
  { value: "confirmed", label: "Potvrzeno" },
  { value: "deposit_paid", label: "Záloha zaplacena" },
  { value: "fully_paid", label: "Plně zaplaceno" },
  { value: "completed", label: "Dokončeno" },
  { value: "cancelled", label: "Zrušeno" },
];

const REFERENCE_FIELDS = [
  { value: "trip_start_date", label: "Začátek zájezdu" },
  { value: "trip_end_date", label: "Konec zájezdu" },
  { value: "deposit_due_date", label: "Splatnost zálohy" },
  { value: "created_at", label: "Vytvoření objednávky" },
];

const ACTION_TYPES = [
  { value: "send_email", label: "Odeslat email", icon: Mail },
  { value: "generate_pdf", label: "Vygenerovat PDF", icon: FileText },
  { value: "slack_notification", label: "Slack notifikace", icon: MessageSquare },
  { value: "change_status", label: "Změnit stav", icon: RefreshCw },
  { value: "assign_guide", label: "Přiřadit průvodce", icon: User },
  { value: "webhook", label: "Webhook", icon: Globe },
];

export default function WorkflowEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === undefined || id === "new";

  const [form, setForm] = useState<{
    name: string;
    description: string;
    triggerType: "status_change" | "time_based" | "manual";
    triggerConfig: {
      from_status: string[];
      to_status: string[];
      reference_field: string;
      days_offset: number;
      time_of_day: string;
      only_if_status: string[];
    };
    actions: WorkflowAction[];
    isActive: boolean;
    priority: number;
  }>({
    name: "",
    description: "",
    triggerType: "status_change",
    triggerConfig: {
      from_status: [],
      to_status: [],
      reference_field: "trip_start_date",
      days_offset: -7,
      time_of_day: "09:00",
      only_if_status: [],
    },
    actions: [],
    isActive: true,
    priority: 0,
  });

  // Fetch workflow if editing
  const { data: workflowData, isLoading } = useQuery({
    queryKey: ["workflow", id],
    queryFn: () => api.get<ApiResponse<Workflow>>(`/api/admin/workflows/${id}`),
    enabled: !isNew,
  });

  // Fetch templates for email/pdf actions
  const { data: templatesData } = useQuery({
    queryKey: ["templates-for-workflow"],
    queryFn: () => api.get<ApiResponse<Template[]>>("/api/admin/templates"),
  });

  const templates = templatesData?.data || [];
  const emailTemplates = templates.filter((t) => t.type === "email");

  // Load workflow data into form
  useEffect(() => {
    if (workflowData?.data) {
      const w = workflowData.data;
      setForm({
        name: w.name,
        description: w.description || "",
        triggerType: w.triggerType,
        triggerConfig: {
          from_status: (w.triggerConfig?.from_status as string[]) || [],
          to_status: (w.triggerConfig?.to_status as string[]) || [],
          reference_field: (w.triggerConfig?.reference_field as string) || "trip_start_date",
          days_offset: (w.triggerConfig?.days_offset as number) || -7,
          time_of_day: (w.triggerConfig?.time_of_day as string) || "09:00",
          only_if_status: (w.triggerConfig?.only_if_status as string[]) || [],
        },
        actions: w.actions,
        isActive: w.isActive,
        priority: w.priority,
      });
    }
  }, [workflowData]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: typeof form) => {
      // Build trigger config based on type
      let triggerConfig: unknown = null;
      if (data.triggerType === "status_change") {
        triggerConfig = {
          from_status: data.triggerConfig.from_status.length > 0 ? data.triggerConfig.from_status : undefined,
          to_status: data.triggerConfig.to_status,
        };
      } else if (data.triggerType === "time_based") {
        triggerConfig = {
          reference_field: data.triggerConfig.reference_field,
          days_offset: data.triggerConfig.days_offset,
          time_of_day: data.triggerConfig.time_of_day,
          only_if_status: data.triggerConfig.only_if_status.length > 0 ? data.triggerConfig.only_if_status : undefined,
        };
      }

      const payload = {
        name: data.name,
        description: data.description || undefined,
        triggerType: data.triggerType,
        triggerConfig,
        actions: data.actions,
        isActive: data.isActive,
        priority: data.priority,
      };

      if (isNew) {
        return api.post<ApiResponse<Workflow>>("/api/admin/workflows", payload);
      } else {
        return api.put<ApiResponse<Workflow>>(`/api/admin/workflows/${id}`, payload);
      }
    },
    onSuccess: () => {
      toast.success(isNew ? "Workflow vytvořeno" : "Workflow uloženo");
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
      navigate("/workflows");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Nepodařilo se uložit workflow");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name) {
      toast.error("Vyplňte název workflow");
      return;
    }

    if (form.triggerType === "status_change" && form.triggerConfig.to_status.length === 0) {
      toast.error("Vyberte alespoň jeden cílový stav");
      return;
    }

    if (form.actions.length === 0) {
      toast.error("Přidejte alespoň jednu akci");
      return;
    }

    saveMutation.mutate(form);
  };

  const addAction = (type: WorkflowAction["type"]) => {
    const newAction: WorkflowAction = { type };

    switch (type) {
      case "send_email":
        newAction.to = "customer";
        break;
      case "generate_pdf":
        newAction.document_type = "contract";
        newAction.send_email = false;
        break;
      case "change_status":
        newAction.new_status = "confirmed";
        break;
      case "webhook":
        newAction.url = "";
        newAction.method = "POST";
        break;
    }

    setForm((prev) => ({
      ...prev,
      actions: [...prev.actions, newAction],
    }));
  };

  const updateAction = (index: number, updates: Partial<WorkflowAction>) => {
    setForm((prev) => ({
      ...prev,
      actions: prev.actions.map((a, i) => (i === index ? { ...a, ...updates } : a)),
    }));
  };

  const removeAction = (index: number) => {
    setForm((prev) => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index),
    }));
  };

  const toggleStatus = (status: string, field: "from_status" | "to_status" | "only_if_status") => {
    setForm((prev) => ({
      ...prev,
      triggerConfig: {
        ...prev.triggerConfig,
        [field]: prev.triggerConfig[field].includes(status)
          ? prev.triggerConfig[field].filter((s) => s !== status)
          : [...prev.triggerConfig[field], status],
      },
    }));
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/workflows")}
          className="btn-ghost"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isNew ? "Nové workflow" : "Upravit workflow"}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic info */}
        <div className="card">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            Základní informace
          </h2>

          <div className="space-y-4">
            <div>
              <label className="label">Název *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                className="input"
                placeholder="např. Potvrzení objednávky"
              />
            </div>

            <div>
              <label className="label">Popis</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                className="input"
                rows={2}
                placeholder="Volitelný popis workflow"
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Aktivní</span>
              </label>

              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700 dark:text-gray-300">Priorita:</label>
                <input
                  type="number"
                  value={form.priority}
                  onChange={(e) => setForm((prev) => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  className="input w-20"
                  min="0"
                  max="100"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Trigger */}
        <div className="card">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            Spouštěč (Trigger)
          </h2>

          {/* Trigger type selector */}
          <div className="mb-6 flex gap-4">
            {[
              { value: "status_change", label: "Změna stavu", icon: Zap },
              { value: "time_based", label: "Časový", icon: Clock },
              { value: "manual", label: "Ruční", icon: Hand },
            ].map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, triggerType: value as typeof form.triggerType }))}
                className={clsx(
                  "flex flex-1 flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors",
                  form.triggerType === value
                    ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                    : "border-gray-200 hover:border-gray-300 dark:border-gray-700"
                )}
              >
                <Icon className={clsx(
                  "h-6 w-6",
                  form.triggerType === value ? "text-primary-600" : "text-gray-400"
                )} />
                <span className={clsx(
                  "text-sm font-medium",
                  form.triggerType === value ? "text-primary-700 dark:text-primary-400" : "text-gray-600 dark:text-gray-400"
                )}>
                  {label}
                </span>
              </button>
            ))}
          </div>

          {/* Status change config */}
          {form.triggerType === "status_change" && (
            <div className="space-y-4">
              <div>
                <label className="label">Z jakého stavu (volitelné)</label>
                <div className="flex flex-wrap gap-2">
                  {ORDER_STATUSES.map((status) => (
                    <button
                      key={status.value}
                      type="button"
                      onClick={() => toggleStatus(status.value, "from_status")}
                      className={clsx(
                        "rounded-full px-3 py-1 text-sm transition-colors",
                        form.triggerConfig.from_status.includes(status.value)
                          ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400"
                      )}
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500">Prázdné = jakýkoli stav</p>
              </div>

              <div>
                <label className="label">Na jaký stav *</label>
                <div className="flex flex-wrap gap-2">
                  {ORDER_STATUSES.map((status) => (
                    <button
                      key={status.value}
                      type="button"
                      onClick={() => toggleStatus(status.value, "to_status")}
                      className={clsx(
                        "rounded-full px-3 py-1 text-sm transition-colors",
                        form.triggerConfig.to_status.includes(status.value)
                          ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400"
                      )}
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Time based config */}
          {form.triggerType === "time_based" && (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label">Vztažené k</label>
                  <select
                    value={form.triggerConfig.reference_field}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      triggerConfig: { ...prev.triggerConfig, reference_field: e.target.value },
                    }))}
                    className="input"
                  >
                    {REFERENCE_FIELDS.map((field) => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label">Počet dní</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={form.triggerConfig.days_offset}
                      onChange={(e) => setForm((prev) => ({
                        ...prev,
                        triggerConfig: { ...prev.triggerConfig, days_offset: parseInt(e.target.value) || 0 },
                      }))}
                      className="input w-24"
                    />
                    <span className="text-sm text-gray-500">
                      {form.triggerConfig.days_offset < 0 ? "dní před" : form.triggerConfig.days_offset > 0 ? "dní po" : "v den"}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="label">Čas spuštění</label>
                <input
                  type="time"
                  value={form.triggerConfig.time_of_day}
                  onChange={(e) => setForm((prev) => ({
                    ...prev,
                    triggerConfig: { ...prev.triggerConfig, time_of_day: e.target.value },
                  }))}
                  className="input w-32"
                />
              </div>

              <div>
                <label className="label">Pouze pokud objednávka je ve stavu</label>
                <div className="flex flex-wrap gap-2">
                  {ORDER_STATUSES.map((status) => (
                    <button
                      key={status.value}
                      type="button"
                      onClick={() => toggleStatus(status.value, "only_if_status")}
                      className={clsx(
                        "rounded-full px-3 py-1 text-sm transition-colors",
                        form.triggerConfig.only_if_status.includes(status.value)
                          ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400"
                      )}
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500">Prázdné = jakýkoli stav</p>
              </div>
            </div>
          )}

          {/* Manual trigger info */}
          {form.triggerType === "manual" && (
            <p className="text-sm text-gray-500">
              Ruční trigger se spouští pouze přes tlačítko v detailu objednávky nebo přes API.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="card">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            Akce
          </h2>

          {form.actions.length === 0 ? (
            <p className="mb-4 text-sm text-gray-500">
              Zatím žádné akce. Přidejte akce, které se provedou při spuštění workflow.
            </p>
          ) : (
            <div className="mb-4 space-y-4">
              {form.actions.map((action, index) => {
                const actionType = ACTION_TYPES.find((t) => t.value === action.type);
                const Icon = actionType?.icon || Zap;

                return (
                  <div
                    key={index}
                    className="flex items-start gap-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                  >
                    <GripVertical className="mt-1 h-5 w-5 cursor-move text-gray-400" />

                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
                      <Icon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    </div>

                    <div className="flex-1 space-y-3">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {actionType?.label || action.type}
                      </div>

                      {/* Action-specific fields */}
                      {action.type === "send_email" && (
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div>
                            <label className="label">Šablona</label>
                            <select
                              value={action.template_id || ""}
                              onChange={(e) => updateAction(index, { template_id: e.target.value })}
                              className="input"
                            >
                              <option value="">Vyberte šablonu</option>
                              {emailTemplates.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="label">Příjemce</label>
                            <select
                              value={action.to || "customer"}
                              onChange={(e) => updateAction(index, { to: e.target.value as "customer" | "admin" | "guide" })}
                              className="input"
                            >
                              <option value="customer">Zákazník</option>
                              <option value="admin">Admin</option>
                              <option value="guide">Průvodce</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {action.type === "generate_pdf" && (
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div>
                            <label className="label">Typ dokumentu</label>
                            <select
                              value={action.document_type || "contract"}
                              onChange={(e) => updateAction(index, { document_type: e.target.value as "contract" | "invoice" })}
                              className="input"
                            >
                              <option value="contract">Smlouva</option>
                              <option value="invoice">Faktura</option>
                            </select>
                          </div>
                          <div className="flex items-end">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={action.send_email || false}
                                onChange={(e) => updateAction(index, { send_email: e.target.checked })}
                                className="h-4 w-4 rounded border-gray-300"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">Odeslat emailem</span>
                            </label>
                          </div>
                        </div>
                      )}

                      {action.type === "slack_notification" && (
                        <div>
                          <label className="label">Zpráva</label>
                          <input
                            type="text"
                            value={action.message_template || ""}
                            onChange={(e) => updateAction(index, { message_template: e.target.value })}
                            className="input"
                            placeholder="{{orderNumber}}: {{status}}"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Použijte proměnné: {"{{orderNumber}}"}, {"{{status}}"}, {"{{firstName}}"}, {"{{tripName}}"}
                          </p>
                        </div>
                      )}

                      {action.type === "change_status" && (
                        <div>
                          <label className="label">Nový stav</label>
                          <select
                            value={action.new_status || "confirmed"}
                            onChange={(e) => updateAction(index, { new_status: e.target.value })}
                            className="input"
                          >
                            {ORDER_STATUSES.map((status) => (
                              <option key={status.value} value={status.value}>
                                {status.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {action.type === "webhook" && (
                        <div className="space-y-3">
                          <div className="grid gap-3 sm:grid-cols-4">
                            <div>
                              <label className="label">Metoda</label>
                              <select
                                value={action.method || "POST"}
                                onChange={(e) => updateAction(index, { method: e.target.value as "POST" | "GET" })}
                                className="input"
                              >
                                <option value="POST">POST</option>
                                <option value="GET">GET</option>
                              </select>
                            </div>
                            <div className="sm:col-span-3">
                              <label className="label">URL</label>
                              <input
                                type="url"
                                value={action.url || ""}
                                onChange={(e) => updateAction(index, { url: e.target.value })}
                                className="input"
                                placeholder="https://..."
                              />
                            </div>
                          </div>
                          {action.method === "POST" && (
                            <div>
                              <label className="label">Body (JSON)</label>
                              <textarea
                                value={action.body_template || ""}
                                onChange={(e) => updateAction(index, { body_template: e.target.value })}
                                className="input font-mono text-sm"
                                rows={3}
                                placeholder='{"order_id": "{{orderNumber}}"}'
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => removeAction(index)}
                      className="btn-ghost btn-sm text-error-600 hover:bg-error-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add action buttons */}
          <div className="flex flex-wrap gap-2">
            {ACTION_TYPES.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => addAction(value as WorkflowAction["type"])}
                className="btn-ghost text-sm"
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate("/workflows")}
            className="btn-ghost"
          >
            Zrušit
          </button>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="btn-primary"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Save className="h-5 w-5" />
            )}
            {isNew ? "Vytvořit" : "Uložit"}
          </button>
        </div>
      </form>
    </div>
  );
}
